import { Button, Checkbox, Menu, Modal, TextInput, Tooltip } from "@mantine/core"
import IconLogin from "@tabler/icons-react/dist/esm/icons/IconLogin"
import IconLogout from "@tabler/icons-react/dist/esm/icons/IconLogout"
import IconNewSection from "@tabler/icons-react/dist/esm/icons/IconNewSection"
import IconUser from "@tabler/icons-react/dist/esm/icons/IconUser"
import IconUserEdit from "@tabler/icons-react/dist/esm/icons/IconUserEdit"
import IconUserFilled from "@tabler/icons-react/dist/esm/icons/IconUserFilled"
import { h } from "preact"
import { useLocation } from "preact-iso"
import { useCallback, useEffect, useRef, useState } from "preact/hooks"

import pub_sub from "../pub_sub"
import { ROUTES } from "../routes"
import { local_storage } from "../state/local_storage"
import { app_store } from "../state/store"
import { EditUserName } from "./EditUserName"
import "./Header.css"
import Loading from "./Loading"


export default function Header()
{
    const location = useLocation()
    const [show_user_options_dropdown, set_show_user_options_dropdown] = useState(false)
    const toggle_show_user_options_dropdown = () => set_show_user_options_dropdown(!show_user_options_dropdown)

    return (
        <header className="header-bar">
            <a href="/" style={{ textDecoration: "none", color: "inherit" }}>
                <h1 style={{ lineHeight: 0, padding: "0 10px" }}>
                    WikiSim
                    <Tooltip label={<>WikiSim is currently in alpha.<br/>Please report any bugs or issues you encounter, and share suggestions via the<br/>GitHub repo or BlueSky linked at the bottom of the page.</>} position="bottom">
                        <span class="wikisim-alpha-label">alpha</span>
                    </Tooltip>
                </h1>
            </a>

            {!location.path.startsWith(ROUTES.DATA_COMPONENT.SEARCH()) && <SearchBar />}

            <nav className="right-user-and-dropdown">
                <UserSession
                    toggle_show_user_options_dropdown={toggle_show_user_options_dropdown}
                />
                <DropDownMenu
                    opened={show_user_options_dropdown}
                    set_opened={set_show_user_options_dropdown}
                />
            </nav>

        </header>
    )
}


function SearchBar()
{
    const location = useLocation()
    const [search_query, set_search_query] = useState("")
    const text_input_ref = useRef<HTMLInputElement | null>(null)

    useEffect(() =>
    {
        return pub_sub.sub("key_down", data =>
        {
            if (data.key === "k" && data.ctrlKey)
            {
                // Focus the search input when Ctrl+K is pressed
                text_input_ref.current?.focus()
                text_input_ref.current?.select() // Select the text in the input
            }
        })
    }, [])

    const navigate_to_search = () => location.route(ROUTES.DATA_COMPONENT.SEARCH({ search_query }))

    return (
        <div className="search-bar">
            <TextInput
                ref={text_input_ref}
                className="search-input"
                value={search_query}
                onChange={(e: h.JSX.TargetedEvent<HTMLInputElement, Event>) =>
                {
                    set_search_query(e.currentTarget.value)
                }}
                onKeyDown={(e: h.JSX.TargetedKeyboardEvent<HTMLInputElement>) =>
                {
                    if (e.key === "Enter") navigate_to_search()
                    if (e.key === "Escape") text_input_ref.current?.blur() // Remove focus from the input
                }}
                onFocus={() => text_input_ref.current?.select() }
                placeholder={"Search WikiSim... (Ctrl+K)"}
            />
            <Button
                variant={"outline"}
                onClick={navigate_to_search}
            >
                {search_query.trim() ? "Search" : "Browse"}
            </Button>
        </div>
    )
}


function UserSession(props: { toggle_show_user_options_dropdown: () => void })
{
    const { user_auth_session, ui } = app_store()
    const { status } = user_auth_session
    const { show_log_in_modal, set_show_log_in_modal } = ui

    return (
        <div
            className="user-session"
            disabled={status !== "logged_in" && status !== "logged_out"}
            onPointerDown={_e =>
            {
                if (status === "logged_in") props.toggle_show_user_options_dropdown()
                else if (status === "logged_out" || status === "logged_out__OTP_sign_in_request_errored") set_show_log_in_modal(true)
                else console.warn("User session is not in a valid state for login/logout:", status)
            }}
        >
            <div className="user-session-status">
                {status === "logged_in" ? <>{user_auth_session.user_name ? user_auth_session.user_name : <Loading />}</>
                : status === "logged_out" ? "Log in / Sign up"
                : status === "logged_out__requesting_OTP_sign_in" ? <>Requesting login<Loading /></>
                : status === "logged_out__OTP_sign_in_request_made" ? "Please check your email"
                : status === "logged_out__OTP_sign_in_request_errored" ? "Log in request failed"
                : <>Loading<Loading /></>}
            </div>
            {status === "logged_in" ? <IconUser /> : <IconLogin />}

            {show_log_in_modal && <LogInModal on_close={() => set_show_log_in_modal(false)} />}
        </div>
    )
}


function LogInModal({ on_close }: { on_close: () => void })
{
    const { user_auth_session } = app_store()
    const { status } = user_auth_session

    const initial_email_address = useRef(local_storage.get_account_email_address().trim())
    const have_initial_email_address = initial_email_address.current.length > 0
    const [email_address, set_email_address] = useState(initial_email_address.current)
    const [remember_me, set_remember_me] = useState(have_initial_email_address)

    useEffect(() =>
    {
        local_storage.set_account_email_address("")
        if (!email_address.trim() || !remember_me) return
        // Save email address to local storage so it can be pre-filled next time
        local_storage.set_account_email_address(email_address.trim())
    }, [email_address, remember_me])

    const [link_requested, set_link_requested] = useState(false)


    const submit_login_form = useCallback((e: h.JSX.TargetedEvent<HTMLFormElement, Event>) =>
    {
        e.preventDefault()
        user_auth_session.request_OTP_sign_in(email_address)
        set_link_requested(true)
    }, [email_address, user_auth_session])


    return (
        <Modal
            opened={true}
            onClose={on_close}
            centered
            size="lg"
        >
            <h2>Log In / Sign Up</h2>

            <form onSubmit={submit_login_form}>
                <TextInput
                    label="Email address"
                    type="email"
                    name="email"
                    autocomplete="username"
                    value={email_address}
                    onChange={(e: h.JSX.TargetedEvent<HTMLInputElement, Event>) =>
                    {
                        set_email_address(e.currentTarget.value)
                    }}
                    placeholder="your@email.com"
                    required
                />

                {remember_me && !have_initial_email_address && <input
                    type="password"
                    autocomplete="new-password"
                    style="position: absolute; left: -10000px; top: -10000px;"
                    aria-hidden="true"
                    tabIndex={-1}
                    value="trigger-save-email-address"
                />}

                <div class="vertical-gap" />

                <Checkbox
                    label="Remember me"
                    checked={remember_me}
                    onChange={(e: h.JSX.TargetedEvent<HTMLInputElement, Event>) =>
                    {
                        set_remember_me(e.currentTarget.checked)
                    }}
                />

                <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
                    {!link_requested ? <Button
                        type="submit"
                        disabled={!email_address.trim()}
                    >
                        Request log in / sign up link
                    </Button>
                    : (
                        status === "logged_out__requesting_OTP_sign_in" ? <>Requesting log in / sign up link<Loading /></>
                        : status === "logged_out__OTP_sign_in_request_made" ? "Please check your email"
                        : status === "logged_out__OTP_sign_in_request_errored" ? "Log in request failed"
                        : <span></span>
                    )}

                    {/* <Button
                        onClick={on_close}
                        variant="outline"
                    >
                        {!link_requested ? "Cancel" : "Close"}
                    </Button> */}
                </div>
            </form>
        </Modal>
    )
}


function DropDownMenu(props: { opened: boolean, set_opened: (opened: boolean) => void })
{
    const { user_auth_session } = app_store()

    const [opened_edit_user_name, set_opened_edit_user_name] = useState(false)


    const allow_create_a_new_wiki_page = !!user_auth_session.session

    // const go_to_users_pages = user_auth_session.session ? () =>
    // {
    //     const user_id = user_auth_session.session!.user.id
    //     // Navigate to search page filterd by user's pages
    //     location.route(ROUTES.DATA_COMPONENT.SEARCH({ user_id }))
    // } : undefined

    // Navigate to user's profile page
    const route_to_profile = user_auth_session.session
        ? ROUTES.USER.VIEW(user_auth_session.session.user.id)
        : undefined

    // Have to use these ugly nested divs to fix https://github.com/wikisim/wikisim-frontend/issues/9
    // Even when the menu has opened=false it still has some area that can be clicked onto
    return <div style={{ width: 3 }}>
    <div style={{ display: props.opened ? undefined : "none" }}>
    <Menu
        shadow="md"
        width={200}
        opened={props.opened}
        onChange={props.set_opened}
    >
        <Menu.Target>
            {/* This ensures the menu is positioned correctly at the location of
            this &nbsp; ... it is a bit of a hack */}
            <div>&nbsp;</div>
        </Menu.Target>

        <Menu.Dropdown>
            {allow_create_a_new_wiki_page && <a
                href={ROUTES.DATA_COMPONENT.NEW()}
                style={{ textDecoration: "none", color: "inherit" }}
            >
                <Menu.Item leftSection={<IconNewSection size={14} />}>
                    Create a new page
                </Menu.Item>
            </a>}

            {/* {go_to_users_pages && <Menu.Item
                leftSection={<IconNotebook size={14} />}
                onClick={go_to_users_pages}
            >
                Your pages (public)
            </Menu.Item>} */}

            {route_to_profile && <a
                href={route_to_profile}
                style={{ textDecoration: "none", color: "inherit" }}
            >
                <Menu.Item leftSection={<IconUserFilled size={14} />}>
                    Profile
                </Menu.Item>
            </a>}

            <Menu.Item
                leftSection={<IconUserEdit size={14} />}
                onClick={() => set_opened_edit_user_name(true)}
            >
                Edit user name
            </Menu.Item>

            <Menu.Item
                leftSection={<IconLogout size={14} />}
                onClick={() =>
                {
                    user_auth_session.logout()
                }}
            >
                Log Out
            </Menu.Item>
        </Menu.Dropdown>
    </Menu>
    <EditUserNameModal opened={opened_edit_user_name} on_close={() => set_opened_edit_user_name(false)} />
    </div>
    </div>
}


function EditUserNameModal(props: { opened: boolean, on_close: () => void })
{
    return <Modal
        opened={props.opened}
        onClose={props.on_close}
        centered
        size="lg"
        title={<h2>Edit your user name</h2>}
    >
        <div className="vertical-gap" />

        <EditUserName />

        <div className="vertical-gap" />
    </Modal>
}
